/**
 * data-mt-theme on <html> (plan §3.4 · F17).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type JSX,
  type ReactNode,
} from 'react';

import { readStoredTheme, writeStoredTheme, type ThemePreference } from './use-persisted-theme';

export type ThemeName = ThemePreference;

interface ThemeContextValue {
  readonly theme: ThemeName;
  readonly setTheme: (theme: ThemeName) => void;
  readonly toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function applyDomTheme(theme: ThemeName): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-mt-theme', theme);
}

export interface ThemeProviderProps {
  readonly children: ReactNode;
  readonly initialTheme?: ThemeName;
}

export function ThemeProvider({ children, initialTheme }: ThemeProviderProps): JSX.Element {
  const [theme, setThemeState] = useState<ThemeName>(() => {
    if (initialTheme) return initialTheme;
    if (typeof window === 'undefined') return 'dark';
    return readStoredTheme() ?? 'dark';
  });

  useEffect(() => {
    applyDomTheme(theme);
    writeStoredTheme(theme);
  }, [theme]);

  const setTheme = useCallback((next: ThemeName) => {
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((t) => (t === 'dark' ? 'light' : 'dark'));
  }, []);

  const value = useMemo(() => ({ theme, setTheme, toggleTheme }), [theme, setTheme, toggleTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx === null) {
    throw new Error('useTheme requires ThemeProvider');
  }
  return ctx;
}
